import {
  Role,
  User,
  UserBody,
  UserPasswordsBody,
  MessageObject,
  UserPosition,
} from "@vviinn/user-managment-client-ts";

import { capitalize } from "../helpers/string/capitalize";
import { RoleTitle } from "./role/types";
import { GlobalRolesTitle } from "./types";
import { responseNotification } from "../components/Notifications";
import { globalRolesMapping, parseRoleName } from "./role/helpers";
import {
  RequestHandlerParams,
  assignGlobalUserRole,
  changeUserPassword,
  unassignGlobalUserRole,
  assignUser,
  getCompany,
  removeCompanyMemberById,
  unassignUser,
  addUser,
  addUserCompanyRole,
  addUserGlobalRole,
  deleteUser,
  deleteUserCompanyRole,
  deleteUserGlobalRole,
  fetchUsers,
  setUserPosition,
  changeUserPosition,
  inviteUserToCompany,
} from "../store";
import { MandatoryCompanyId } from "../company";

const actionDescriptions = {
  assignRole: {
    info: "Assigning role",
    successMessage: "Role assigned to user",
    errorMessage: "Could not assign role to user",
  },
  unassignRole: {
    info: "Unassigning role",
    successMessage: "Role unassigned from user",
    errorMessage: "Unable to unassign user",
  },
  changeUserPassword: {
    info: "Changing password",
    successMessage: "Password changed",
    errorMessage: "Could not change password",
  },
  createUser: {
    info: "Creating user",
    successMessage: "User created",
    errorMessage: "Could not create user",
  },
  setUserPosition: {
    info: "Changing user role",
    successMessage: "User role changed",
    errorMessage: "Could not change user role",
  },
  inviteUser: {
    info: "Sending invitation",
    successMessage: "Invitation sent",
    errorMessage: "Could not send invitation",
  },
};

export const handleUser = (params: RequestHandlerParams) => {
  const { getApi, notify, dispatch } = params;

  const fetchUserCompany = async (userCompanyId: string) => {
    const api = await getApi();
    dispatch(getCompany({ api, value: userCompanyId }));
  };
  const updateUsers = async () => {
    const api = await getApi();
    dispatch(fetchUsers(api));
  };

  const showResponseNotification = (
    isSuccess: boolean,
    messageType: keyof typeof actionDescriptions,
  ) => {
    const { successMessage, errorMessage } = actionDescriptions[messageType];
    responseNotification(notify, isSuccess, {
      successMessage,
      errorMessage,
    });
  };

  return {
    async handleAssignCompanyRole(
      userId: User["uid"],
      role: Role,
      currentCompanyId: string,
      companyId: string,
    ) {
      const api = await getApi();
      notify("Info", actionDescriptions.assignRole.info);
      const request = await dispatch(
        assignUser({
          api,
          value: {
            companyId,
            userId,
            role,
          },
        }),
      );
      showResponseNotification(!!request.payload, "assignRole");
      if (!request.payload) return request.payload;
      dispatch(addUserCompanyRole({ userId, id: companyId, role }));
      if (currentCompanyId === companyId) {
        dispatch(getCompany({ api, value: currentCompanyId }));
      }
      return request.payload;
    },

    async handleAssignGlobalRole(userId: User["uid"], role: RoleTitle) {
      const api = await getApi();
      notify("Info", actionDescriptions.assignRole.info);
      const response = await dispatch(
        assignGlobalUserRole({
          api,
          value: { userId, role: role.role },
        }),
      );
      if (response.payload) {
        const parsedRole = parseRoleName(role);
        const roleName = parsedRole
          .split(" ")
          .map((string) => capitalize(string))
          .join(" ");
        dispatch(addUserGlobalRole({ role: roleName, userId }));
      }
      showResponseNotification(!!response.payload, "assignRole");
      return response.payload;
    },

    async handleUnassignCompanyRole(
      role: Role,
      userId: string,
      currentCompanyId: string,
      companyId?: string,
    ) {
      const targetCompanyId = companyId ?? currentCompanyId;
      const api = await getApi();
      notify("Info", actionDescriptions.unassignRole.info);
      const response = await dispatch(
        unassignUser({
          api,
          value: { companyId: targetCompanyId, userId, role },
        }),
      );
      if (response.payload) {
        if (companyId) {
          dispatch(deleteUserCompanyRole({ id: companyId, userId }));
          if (companyId === currentCompanyId)
            fetchUserCompany(currentCompanyId);
        } else {
          updateUsers();
          await dispatch(removeCompanyMemberById(userId));
        }
      }
      showResponseNotification(!!response.payload, "unassignRole");
      return response.payload;
    },

    async handleUnassignGlobalRole(userId: string, role: GlobalRolesTitle) {
      const api = await getApi();
      notify("Info", actionDescriptions.unassignRole.info);
      const response = await dispatch(
        unassignGlobalUserRole({
          api,
          value: { userId, role: globalRolesMapping[role] },
        }),
      );
      if (response.payload) {
        dispatch(deleteUserGlobalRole({ role, userId }));
      }
      showResponseNotification(!!response.payload, "unassignRole");
    },

    async handleChangePassword(
      userId: User["uid"],
      userPasswordsBody: UserPasswordsBody,
    ) {
      const api = await getApi();
      notify("Info", actionDescriptions.changeUserPassword.info);
      const response = await dispatch(
        changeUserPassword({ api, value: { userId, userPasswordsBody } }),
      );
      showResponseNotification(!!response.payload, "changeUserPassword");
      return !!response.payload;
    },

    async handleSetPosition(
      userId: User["uid"],
      position?: UserPosition["position"],
    ) {
      const api = await getApi();
      notify("Info", actionDescriptions.changeUserPassword.info);
      const response = await dispatch(
        setUserPosition({ api, value: { userId, position } }),
      );
      if (response.payload) {
        dispatch(changeUserPosition({ position, userId }));
      }
      showResponseNotification(!!response.payload, "setUserPosition");
      return !!response.payload;
    },

    // The bff resolves the email server-side: an existing user gets assigned to the
    // company directly, a brand-new email gets sent an invite — either way this is one
    // request and either result is a success from the caller's point of view.
    async handleInviteUser(email: string, companyId: MandatoryCompanyId) {
      const api = await getApi();
      notify("Info", actionDescriptions.inviteUser.info);
      const response = await dispatch(
        inviteUserToCompany({ api, value: { companyId, email } }),
      );
      if (response.payload) {
        dispatch(getCompany({ api, value: companyId }));
      }
      showResponseNotification(!!response.payload, "inviteUser");
      return !!response.payload;
    },

    async handleCreateUser(user: UserBody) {
      const api = await getApi();
      const response = await dispatch(addUser({ api, value: user }));
      if (response.payload) {
        const newUserCredentials = response.payload as User;
        return newUserCredentials.uid;
      }
      return;
    },

    async handleDeleteUser(userId: string, companyToFetch?: string) {
      const api = await getApi();
      const response = await dispatch(deleteUser({ api, value: userId }));
      const deleteUserResult = response.payload as MessageObject;
      const responseResult = deleteUserResult.message === "success";
      if (responseResult) {
        if (companyToFetch) fetchUserCompany(companyToFetch);
      }
      return responseResult;
    },
  };
};
